import { useEffect, useState } from 'react';

import AttributeDetails from '@atom/components/common/attributeDetails/AttributeDetails';
import { AttributeView } from '@atom/components/common/attributeDetails/AttributeDetailsContext';
import textConstants from '@atom/constants/textConstants';
import { Snackbar } from '@atom/mui';
import { AssetDetail, AttributesType } from '@atom/types/inventory';
import { PolicyAction } from '@atom/types/policy';
import { hasAccess } from '@atom/utilities/accessUtilities';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import { getGoogleShapeCenter } from '@atom/utilities/mapUtilities';
import timeUtilities from '@atom/utilities/timeUtilities';

import InfoRow from './InfoRow';

import classes from './mapDrawerContent.module.css';

interface Props {
  asset: AssetDetail;
  refetch: () => void;
  // Denotes if component is being used in work view for add asset and task map views
  workView?: boolean;
}

const AssetSummaryInfo = ({ asset, workView, refetch }: Props) => {
  const [loading, setLoading] = useState<any[]>([]);
  const centerLocation = getGoogleShapeCenter(asset.location);

  const lastWorkOrderName =
    asset.lastWorkOrder && asset.lastWorkOrder.name
      ? asset.lastWorkOrder.name
      : '-';

  const lastWorkOrderUpdatedDate =
    asset.lastWorkOrder && asset.lastWorkOrder.updatedDate
      ? timeUtilities.setDisplayDate(asset.lastWorkOrder.updatedDate)
      : '-';

  useEffect(() => {
    if (!workView) {
      refetch();
    }
  }, []);

  const onAttributesPendingChange = async (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => {
    try {
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
      });

      await api.post(
        `${INVENTORY_ASSETS_ENDPOINT}/${asset.id}/attributes/${attribute.id}/changes/${attribute.changeId}/${action}`,
        { rootAssetId: asset.id },
      );

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });

      refetch();
    } catch (error) {
      Snackbar.error({
        message:
          'Something went wrong. Please try again or contact an administrator.',
      });
    }
  };

  const onAttributesSave = async (payload: any) => {
    try {
      setLoading([payload.attributeGroupName]);

      await api.patch(
        `${INVENTORY_ASSETS_ENDPOINT}/${asset.id}/attributes`,
        payload.attributes,
      );

      refetch();

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message:
          'Something went wrong. Please try again or contact an administrator.',
      });
    }
    setLoading([]);
  };

  const canUpdate = hasAccess(PolicyAction.UPDATE, asset?.actions);
  const canManageChanges = hasAccess(
    PolicyAction.MANAGE_INVENTORY_CHANGES,
    asset?.actions,
  );

  // Attributes need to be pulled out of attributeGroups and turned into a map
  // to work with AttributeDetails component
  const mappedAsset = {
    ...asset,
    attributes: asset.attributeGroups.reduce((acc, attributeGroup) => {
      return {
        ...acc,
        ...attributeGroup.attributes.reduce((innerAcc, attribute) => {
          return { ...innerAcc, [attribute.id]: attribute };
        }, {}),
      };
    }, {}),
  };

  return (
    <div className={classes.infoContainer}>
      <div className={classes.basicInfo}>
        <InfoRow
          icon="location_on"
          main={asset.address}
          sub={`${centerLocation.lat}, ${centerLocation.lng}`}
        />
        <InfoRow
          icon="edit"
          main={timeUtilities.setDisplayDate(asset.updatedDate)}
          sub="Last Updated"
        />
        <InfoRow
          icon="work"
          main={`Work ID: ${lastWorkOrderName}`}
          sub={`Last Updated: ${lastWorkOrderUpdatedDate}`}
        />
      </div>
      <AttributeDetails
        onPendingApproval={onAttributesPendingChange}
        // @ts-ignore
        asset={mappedAsset}
        onAttributeUpdate={onAttributesSave}
        loading={loading}
        canManageChanges={canManageChanges}
        canUpdate={canUpdate}
        view={AttributeView.MAP_TRAY}
        inputWidth="155px"
      />
    </div>
  );
};

export default AssetSummaryInfo;
